import { useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { OrbitControls as ThreeOrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { ArrowsClockwise, Cube, DownloadSimple, X } from "@phosphor-icons/react";
import { BASE_H, Island } from "@/components/site/Island3D";

const THICK = { "2cm": 0.79, "3cm": 1.18, "1.2cm": 0.47, "6cm": 2.36 };
const thickOf = (t) =>
  THICK[
    String(t || "")
      .replace(/\s/g, "")
      .toLowerCase()
  ] || 1.18;
const LEAN = 1.32; // radians the slab leans back when standing, like on an A-frame

// Soft, blurred room reflections: a sharp bright-panel environment mirrored off a
// polished face blows the veining out to white (the "faded front face" bug).
const Studio = () => {
  const { gl, scene } = useThree();
  useEffect(() => {
    gl.toneMapping = THREE.NeutralToneMapping;
    gl.toneMappingExposure = 1;
    const pmrem = new THREE.PMREMGenerator(gl);
    const env = pmrem.fromScene(new RoomEnvironment(), 0.45).texture;
    scene.environment = env;
    scene.environmentIntensity = 0.35;
    return () => {
      scene.environment = null;
      env.dispose();
      pmrem.dispose();
    };
  }, [gl, scene]);
  return null;
};

const Orbit = ({ target, span }) => {
  const { camera, gl } = useThree();
  const ref = useRef(null);
  useEffect(() => {
    const c = new ThreeOrbitControls(camera, gl.domElement);
    c.enableDamping = true;
    c.dampingFactor = 0.08;
    c.maxPolarAngle = Math.PI / 2 - 0.02;
    c.minDistance = span * 0.35;
    c.maxDistance = span * 4;
    ref.current = c;
    return () => c.dispose();
  }, [camera, gl, span]);
  useEffect(() => {
    ref.current?.target.set(...target);
    ref.current?.update();
  }, [target]);
  useFrame(() => ref.current?.update());
  return null;
};

const Fit = ({ span, mode, Wd }) => {
  const { camera, size } = useThree();
  const aspect = size.width / Math.max(1, size.height);
  useEffect(() => {
    // pull back on narrow (portrait) canvases so the whole slab stays in frame
    const d = (span * 1.2 + 20) * Math.max(1, 1.45 / aspect);
    if (mode === "stand")
      camera.position.set(d * 0.2, Wd * 0.5 + d * 0.12, d * 0.95);
    else if (mode === "island") camera.position.set(d * 0.7, 66, d * 1.05);
    else camera.position.set(d * 0.45, d * 0.8, d * 0.9);
    camera.near = 0.5;
    camera.far = 8000;
    camera.updateProjectionMatrix();
  }, [camera, span, mode, Wd, aspect]);
  return null;
};

const Capture = ({ api }) => {
  const get = useThree((s) => s.get);
  useEffect(() => {
    api.current = () => {
      const { gl, scene, camera } = get();
      gl.render(scene, camera);
      return gl.domElement.toDataURL("image/png");
    };
    return () => {
      api.current = null;
    };
  }, [get, api]);
  return null;
};

const Slab = ({ url, L, Wd, T, mode, finish, flipped, onState }) => {
  const [tex, setTex] = useState(null);
  const spin = useRef(null);
  useEffect(() => {
    if (!url) return undefined;
    let alive = true;
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin("anonymous");
    loader.load(
      url,
      (t) => {
        if (!alive) return;
        t.colorSpace = THREE.SRGBColorSpace;
        t.anisotropy = 16;
        t.generateMipmaps = true;
        t.minFilter = THREE.LinearMipmapLinearFilter;
        setTex(t);
        onState("ready");
      },
      undefined,
      () => onState("error"),
    );
    return () => {
      alive = false;
    };
  }, [url, onState]);

  // The photo is the albedo; keep the polished sheen subtle so the veining stays at
  // full contrast instead of being washed out by specular glare.
  const mats = useMemo(() => {
    const polished = finish === "polished";
    const face = new THREE.MeshPhysicalMaterial({
      map: tex,
      color: tex ? "#ffffff" : "#cfcac2",
      side: THREE.FrontSide,
      roughness: polished ? 0.36 : 0.62,
      clearcoat: polished ? 0.25 : 0,
      clearcoatRoughness: 0.32,
      specularIntensity: polished ? 0.4 : 0.25,
      envMapIntensity: polished ? 0.4 : 0.25,
    });
    let side = null;
    if (tex) {
      side = tex.clone();
      side.wrapS = THREE.RepeatWrapping;
      side.wrapT = THREE.RepeatWrapping;
      side.repeat.set(1, T / Wd);
      side.needsUpdate = true;
    }
    const rim = new THREE.MeshPhysicalMaterial({
      map: side,
      color: tex ? "#e6e2db" : "#bdb7ae",
      side: THREE.FrontSide,
      roughness: polished ? 0.4 : 0.65,
      clearcoat: polished ? 0.25 : 0,
      clearcoatRoughness: 0.32,
      envMapIntensity: 0.4,
    });
    return [rim, rim, face, face, rim, rim]; // +x -x +y -y +z -z
  }, [tex, finish, T, Wd]);

  // Flip turns the slab over around its own short axis (eased), so the other face
  // comes round to the camera in every mode.
  useFrame((_, delta) => {
    if (!spin.current) return;
    const goal = flipped ? Math.PI : 0;
    spin.current.rotation.z = THREE.MathUtils.damp(
      spin.current.rotation.z,
      goal,
      7,
      delta,
    );
  });

  const stand = mode === "stand";
  const lift = stand
    ? (Wd / 2) * Math.sin(LEAN) + T
    : mode === "island"
      ? BASE_H + T / 2
      : T / 2;
  // +LEAN tips the far edge up so the photographed (top) face leans back toward the
  // camera; -LEAN would show the underside, whose texture reads mirrored.
  return (
    <group
      position={[0, lift, stand ? (Wd / 2) * Math.cos(LEAN) * 0.2 : 0]}
      rotation={[stand ? LEAN : 0, 0, 0]}
    >
      <group ref={spin}>
        <mesh castShadow receiveShadow material={mats}>
          <boxGeometry args={[L, T, Wd]} />
        </mesh>
      </group>
    </group>
  );
};

// Public product page: the slab alone in a light studio — orbit, flat or standing,
// polished or honed, snapshot.
export const Slab3D = ({
  photo,
  lengthIn,
  widthIn,
  thickness,
  title,
  onClose,
}) => {
  const known = Number(lengthIn) > 0 && Number(widthIn) > 0;
  const L = known ? Number(lengthIn) : 120;
  const Wd = known ? Number(widthIn) : 70;
  const T = thickOf(thickness);
  const span = Math.max(L, Wd);
  const [mode, setMode] = useState("stand");
  const [finish, setFinish] = useState("polished");
  const [flipped, setFlipped] = useState(false);
  const [state, setState] = useState(photo ? "loading" : "ready");
  const capture = useRef(null);
  const island = mode === "island";

  useEffect(() => {
    const key = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);

  const snapshot = () => {
    const url = capture.current?.();
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(title || "slab").replace(/\s+/g, "-")}-3d.png`;
    a.click();
  };

  const target = useMemo(
    () =>
      mode === "stand"
        ? [0, Wd * 0.42, 0]
        : mode === "island"
          ? [0, 30, 0]
          : [0, 0, 0],
    [mode, Wd],
  );
  const seg = (on) =>
    `h-9  px-3 text-xs font-semibold transition-colors duration-200 ${
      on ? "bg-white text-[#1c1b19]" : "text-white/70 hover:text-white"
    }`;

  return (
    <div
      className="site site-overlay fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-8"
      data-testid="slab-3d"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${title} 3D view`}
    >
      <div
        className="flex max-h-full w-full max-w-[94vw] flex-col overflow-hidden  bg-white"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <div className="flex flex-wrap items-center gap-3 bg-[#1c1b19] px-5 py-3 text-white">
          <span className="flex items-center gap-2 text-sm font-medium">
            <Cube size={16} weight="bold" /> {title} · 3D view
          </span>
          <div className="ml-auto flex items-center gap-1  bg-[#3a3835] p-1">
            <button
              type="button"
              data-testid="slab-3d-flat"
              onClick={() => setMode("flat")}
              className={seg(mode === "flat")}
            >
              Lay flat
            </button>
            <button
              type="button"
              data-testid="slab-3d-stand"
              onClick={() => setMode("stand")}
              className={seg(mode === "stand")}
            >
              Stand up
            </button>
            <button
              type="button"
              data-testid="slab-3d-island"
              onClick={() => setMode("island")}
              className={seg(island)}
            >
              On an island
            </button>
          </div>
          <div className="flex items-center gap-1  bg-[#3a3835] p-1">
            <button
              type="button"
              data-testid="slab-3d-polished"
              onClick={() => setFinish("polished")}
              className={seg(finish === "polished")}
            >
              Polished
            </button>
            <button
              type="button"
              data-testid="slab-3d-honed"
              onClick={() => setFinish("honed")}
              className={seg(finish === "honed")}
            >
              Honed
            </button>
          </div>
          <button
            type="button"
            data-testid="slab-3d-flip"
            aria-pressed={flipped}
            onClick={() => setFlipped((f) => !f)}
            className={`flex h-9 items-center gap-2  px-3 text-xs font-semibold transition-colors duration-200 ${
              flipped
                ? "bg-white text-[#1c1b19]"
                : "bg-[#3a3835] hover:bg-[#4a4844]"
            }`}
          >
            <ArrowsClockwise size={14} weight="bold" /> Flip over
          </button>
          <button
            type="button"
            data-testid="slab-3d-snapshot"
            onClick={snapshot}
            className="flex h-9 items-center gap-2  bg-[#3a3835] px-3 text-xs font-semibold hover:bg-[#4a4844]"
          >
            <DownloadSimple size={14} weight="bold" /> Snapshot
          </button>
          <button
            type="button"
            data-testid="slab-3d-close"
            onClick={onClose}
            aria-label="Close"
            className="flex h-9 w-9 items-center justify-center  text-white/70 hover:bg-white/10 hover:text-white"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="relative h-[78vh] bg-[#f3f1ec]">
          <Canvas
            shadows
            dpr={[1, 2]}
            gl={{ antialias: true, preserveDrawingBuffer: true }}
            camera={{ fov: 32, near: 0.5, far: 8000 }}
            data-testid="slab-3d-canvas"
          >
            <color attach="background" args={["#f3f1ec"]} />
            <Studio />
            <Capture api={capture} />
            <Fit span={span} mode={mode} Wd={Wd} />
            <Orbit target={target} span={span} />
            <ambientLight intensity={0.15} />
            <hemisphereLight
              intensity={0.2}
              color="#ffffff"
              groundColor="#d8d1c6"
            />
            <directionalLight
              position={[span * 0.35, span * 1.5, span * 1.1]}
              intensity={0.6}
              castShadow
              shadow-mapSize={[2048, 2048]}
              shadow-bias={-0.0004}
              shadow-camera-left={-span * 1.4}
              shadow-camera-right={span * 1.4}
              shadow-camera-top={span * 1.4}
              shadow-camera-bottom={-span * 1.4}
              shadow-camera-far={span * 5}
            />
            <mesh rotation-x={-Math.PI / 2} position-y={-0.02}>
              <planeGeometry args={[span * 8, span * 8]} />
              <meshStandardMaterial
                color={island ? "#d4bf9a" : "#f4f1eb"}
                roughness={1}
              />
            </mesh>
            <mesh rotation-x={-Math.PI / 2} receiveShadow>
              <planeGeometry args={[span * 8, span * 8]} />
              <shadowMaterial opacity={0.26} />
            </mesh>
            {island && <Island L={L} Wd={Wd} />}
            <Slab
              url={photo}
              L={L}
              Wd={Wd}
              T={T}
              mode={mode}
              finish={finish}
              flipped={flipped}
              onState={setState}
            />
          </Canvas>
          {state === "loading" && (
            <div
              className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-[color:var(--s-mute)]"
              data-testid="slab-3d-loading"
            >
              Loading the slab photo…
            </div>
          )}
          <div
            className="pointer-events-none absolute bottom-4 left-4  bg-white/92 px-3.5 py-2 text-xs font-medium text-[color:var(--s-ink)] backdrop-blur"
            data-testid="slab-3d-dims"
          >
            {L.toFixed(1)}" × {Wd.toFixed(1)}" × {thickness || "3cm"}
            {!known && " · nominal size"}
            {island && ' · 36" counter height · 6 ft figure for scale'}
          </div>
          <div className="pointer-events-none absolute bottom-4 right-4 hidden  bg-white/80 px-3 py-1.5 text-[11px] text-[color:var(--s-mute)] backdrop-blur sm:block">
            Drag to orbit · scroll to zoom · Flip over for the other side
          </div>
        </div>
      </div>
    </div>
  );
};

export default Slab3D;
